import React from 'react';
import { useNavigate } from 'react-router-dom';
import { t } from 'i18next';
import { format } from 'date-fns';
import { Skeleton } from 'primereact/skeleton';
import { PROJECTS, QUESTIONS } from '../../../../shared/routes/path';
import { ChatType } from '../../../../shared/types/monitor/ChatType';
import { useGetPageTrafficPrompts } from '../../../../shared/api/hooks/monitor/data/sources/UseGetPageTrafficPrompts';

interface PageTrafficPromptsProps {
  projectId: number;
  source: string;
  chatType: ChatType | null;
  from: Date | null;
  to: Date | null;
  monitorPromptIds?: number[];
  filterMonitorTypes?: string[];
}

/**
 * Expanded-row content of the URL Overview table: the prompts that cited the URL. PrimeReact renders
 * the expansion template only for expanded rows, so mounting the query here means it fires on
 * expand — the table itself ships without prompts and a 300-row ranking stays light.
 * Markup mirrors the DomainSourcesTable expansion: each prompt is a card deep-linking to the prompt
 * stats page with chatDate + chatId so the Stats accordion opens on the relevant answer.
 */
const PageTrafficPrompts: React.FC<PageTrafficPromptsProps> = ({
  projectId,
  source,
  chatType,
  from,
  to,
  monitorPromptIds,
  filterMonitorTypes,
}) => {
  const navigate = useNavigate();
  const { prompts, isLoading } = useGetPageTrafficPrompts(
    projectId,
    source,
    chatType,
    from,
    to,
    monitorPromptIds,
    filterMonitorTypes
  );

  if (isLoading) {
    // Two bars in the shape of the prompt cards that replace them, so the swap doesn't shift the
    // layout. Same recipe as the global `skeleton--table`: the PrimeReact container only sizes the
    // block, the inner spans carry the shimmer — the height lives in the SCSS class, because the
    // global `div[data-pc-name='skeleton'] { height: 100% !important }` overrides the inline
    // `height` prop and 100% of an expansion with no definite height collapses to nothing.
    return (
      <div className="page-traffic-table__expansion">
        <Skeleton className="page-traffic-table__prompt-skeleton">
          <span />
          <span />
        </Skeleton>
      </div>
    );
  }

  if (prompts.length === 0) {
    return (
      <div className="page-traffic-table__expansion page-traffic-table__expansion--empty">
        {t('noPromptsForSource')}
      </div>
    );
  }

  return (
    <div className="page-traffic-table__expansion">
      <div className="page-traffic-table__expansion-title">{t('promptsCitingThisPage')}</div>
      {prompts.map((prompt) => {
        const chatDate = prompt.createdAt ? format(new Date(prompt.createdAt), 'yyyy-MM-dd') : '';
        return (
          <a
            key={`${prompt.monitorPromptId}-${prompt.monitorDataId}`}
            className="page-traffic-table__prompt-box page-traffic-table__prompt-box--clickable"
            onClick={() => {
              navigate(
                `${PROJECTS}/${projectId}${QUESTIONS}/${prompt.monitorPromptId}/daily/all?chatDate=${chatDate}&chatId=${prompt.monitorDataId}`
              );
            }}
          >
            <div className="page-traffic-table__prompt-content">
              <span className="page-traffic-table__prompt-keyword">{prompt.keyword}</span>
              {prompt.snippet && (
                <span className="page-traffic-table__prompt-snippet">{prompt.snippet}</span>
              )}
            </div>
            <span className="page-traffic-table__prompt-date">{chatDate}</span>
          </a>
        );
      })}
    </div>
  );
};

export default PageTrafficPrompts;
